/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useRef, useState} from "react";
import Dropzone, {IFileWithMeta, StatusValue} from "@cruisecritic/react-dropzone-uploader";

import {IStepUploadFileProps} from "../types";
import {useSelectFileUploadHasErrors, useSelectFileUploadMessages, useSelectWizardMetaData, useSliceActions} from "../../../../redux/ducks/Wizard/SliceProvider";
import {useDispatch} from "react-redux";
import {IMessage} from "../../../pages/radar/SensorRadar/FileProcessingMessages";
import {ARGOS_ERROR} from "../../../../constants/colors";
import TemplateStep from "./TemplateStep";
import {Spinner} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {useMsal} from "@azure/msal-react";
import {requestIdToken} from "../../../../auth/authConfig";
import {Skeleton} from '@mui/material';
import {selectDialogState, selectVehicleProgramsDevelopmentStagesTree, setIsOverrideVersionDataWarningDialogOpen, setOverrideDialogData} from "../../../../redux/ducks/VehicleProgramsDetails/slice";
import {OverrideDataWarningDialog} from "../../Dialogs/VehicleProgramsDetailsDialog/OverrideDataWarningDialog";
import {StyledArgosDialog} from "../../Dialogs/ArgosDialog";
import {useAppSelector} from "../../../../redux/hooks";
import {cloneDeep} from "lodash";

const duplicateSensorsErrorMsg = 'We have detected more than 1 sensor of the same modality at the same location. This configuration is not currently allowed. Please correct and reupload.'

const file_error_statuses = [
  'error_upload', 'aborted', 'rejected_file_type', 'rejected_max_files', 'error_file_size'
]

const fileProcessingLogsToObjects = (messageList: string[][]): IMessage[][] => {
  if (messageList == null || messageList.length == 0) return []
  return messageList.map((lst) => {
    return lst.map(val => {
      return {text: val, color: ''}
    })
  })
}

const FileUploadLoaderIcon = React.memo(({isFileUploading}: { isFileUploading: boolean }) => {
  return (
    <>
      {isFileUploading ? <Spinner animation={'border'} style={{color: 'grey'}}/> :
        <Button className="button-styling" variant="contained" style={{display: "none"}}>Load File</Button>
      }
    </>
  )
});

const StepUploadFile = (props: IStepUploadFileProps) => {
  const id = props?.id
  const {messagesTitle} = props
  const {instance, accounts} = useMsal();
  const dispatch = useDispatch()
  const wizardMetaData = useSelectWizardMetaData()
  const {
    fileUploadClearMessages,
    fileUploadAddMessage,
    setFileUploadResponse,
    setFileUploadHasErrors,
    setFileDataHasStatements,
    setFileDataHasErrors,
    setFileDataHasWarnings,
    setAllErrorMessages,
    setAllWarningMessages,
    setAllStatementMessages,

    setWizardOpen,
    clearState
  } = useSliceActions()
  const statementMessages = useSelectFileUploadMessages()
  const errorMessages: IMessage[] = []
  const warningMessages: IMessage[] = []
  const hasStartedUpload = useRef(false)
  const [isFileUploading, setIsFileUploading] = useState(false);
  const fileUploadHasErrors = useSelectFileUploadHasErrors()

  const resetUploadState = () => {
    hasStartedUpload.current = false
    setIsFileUploading(false)
  }

  const vehicleProgramsDevelopmentStagesTree = useAppSelector(selectVehicleProgramsDevelopmentStagesTree)
  const handleChangeStatus = async (file: IFileWithMeta, status: StatusValue, allFiles: IFileWithMeta[]) => {

    const xhr = file.xhr

    if (['preparing', 'getting_upload_params', 'uploading'].includes(status)) {
      if (!hasStartedUpload.current) {
        dispatch(fileUploadClearMessages())
        const msg = {text: 'Uploading file', color: ''}
        dispatch(fileUploadAddMessage(msg))

        hasStartedUpload.current = true
        setIsFileUploading(true)
        dispatch(setFileUploadHasErrors(false))
      }
      // resetUploadState()
    } else if (file_error_statuses.includes(status)) {

      // checkResponse(instance, xhr, false)
      const msg = {
        text: `There was an error parsing your file, please make sure you are uploading the right file and try again`,
        color: ARGOS_ERROR
      }
      dispatch(fileUploadAddMessage(msg))
      dispatch(setFileUploadHasErrors(true))
      resetUploadState()
    } else if (status === 'done') {
      let response = JSON.parse(await xhr?.response);

      /**
       * First check if this combination of adas program and development stage already exists. If so, show warning dialog.
       */
      let vehicleProgramHasDuplicateSensors = false
      if (wizardMetaData?.wizardType === 'Vehicle Program') {
        const adasProgramId = response?.cosmos_data?.metadata?.['adas_program_id']
        const developmentStageName = response?.cosmos_data?.metadata?.['vehicle_program_development_stage']
        const developmentStageId = response?.cosmos_data?.metadata?.['developmentStageId']
        // TODO: Could change this to check in the backend, and dont update the other state until the user confirms the override.
        const invalidSensors = response.invalidSensors
        const duplicateSensors = response.duplicateSensors
        if (invalidSensors || duplicateSensors) {
          dispatch(setIsOverrideVersionDataWarningDialogOpen(true))
          dispatch(setOverrideDialogData({invalidSensors, duplicateSensors}))
        }
        if (duplicateSensors) {
          vehicleProgramHasDuplicateSensors = true
        }
      }

      let _fileUploadHasErrors = false

      dispatch(setFileUploadHasErrors(false))
      const msg = {text: `Successfully uploaded file`, color: ''}
      dispatch(fileUploadAddMessage(msg))

      resetUploadState()
      let statements = response.statements
      let errors = response.errors
      let firstPageErrorsOnly = false
      let warnings = response.warnings

      if (statements != undefined && statements?.statement_messages != "") {
        const msg = {
          text: response.statement_messages,
          color: "black"
        }
        dispatch(fileUploadAddMessage(msg))
      }

      if (errors != undefined && errors?.error_messages != "") {
        const msg = {
          text: response.error_messages,
          color: ARGOS_ERROR
        }
        dispatch(fileUploadAddMessage(msg))
        // errors = errors.errors
      }

      /**
       * Conditional logic based on the wizard type.
       */
      if (wizardMetaData?.wizardType === 'Vehicle Program') {
        const cosmos_vehicle_program_id = response.cosmos_data.metadata['vehicle_program_id']
        if ((wizardMetaData.vehicleProgramId !== cosmos_vehicle_program_id)) {
          const msg = {
            text: `Invalid file. Vehicle program id in uploaded file does not match current vehicle program id.`,
            color: ARGOS_ERROR
          }
          dispatch(fileUploadAddMessage(msg))
          errors = true
        }
      } else if (wizardMetaData?.wizardType === 'Radar' || wizardMetaData?.wizardType === 'Camera') {
        const isRadar = wizardMetaData?.wizardType === 'Radar'
        const instructions_data = isRadar ? response?.cosmos_data?.instructions_data : response?.cosmos_data
        let supplier = isRadar ? instructions_data?.["supplier"] : instructions_data?.["cameraSupplier"]
        let component = isRadar ? instructions_data?.["component"] : instructions_data?.["cameraModule"]
        const version = isRadar ? instructions_data?.["componentVersion"] : instructions_data?.["version"]
        const sensor_id = response?.sensor_id
        supplier = supplier?.replace("/", " ")
        component = component?.replace("/", " ")
        let inputParams: any[] = []

        // This is so that in the ui we display the proper name based on the sensor type.
        const inputParamToDisplayNameMap: any = {}
        inputParamToDisplayNameMap["sensorId"] = "Sensor Id"
        if (isRadar) {
          inputParamToDisplayNameMap["supplier"] = "Supplier"
          inputParamToDisplayNameMap["component"] = "Component"
          inputParamToDisplayNameMap["version"] = "Component Version"
        } else {
          inputParamToDisplayNameMap["supplier"] = "Camera Supplier"
          inputParamToDisplayNameMap["component"] = "Camera Module"
          inputParamToDisplayNameMap["version"] = "Camera Module Version"
        }

        if (supplier != wizardMetaData?.supplier) {
          inputParams.push({
            "inputParam": inputParamToDisplayNameMap["supplier"],
            "sheetValue": supplier,
            "sensorDBValue": wizardMetaData?.supplier
          })
        }
        if (component != wizardMetaData?.component) {
          inputParams.push({
            "inputParam": inputParamToDisplayNameMap["component"],
            "sheetValue": component,
            "sensorDBValue": wizardMetaData?.component
          })
        }
        if (version != wizardMetaData?.version) {
          inputParams.push({
            "inputParam": inputParamToDisplayNameMap["version"],
            "sheetValue": version,
            "sensorDBValue": wizardMetaData?.version
          })
        }
        if (sensor_id != wizardMetaData?.id) {
          inputParams.push({
            "inputParam": inputParamToDisplayNameMap["sensorId"],
            "sheetValue": sensor_id,
            "sensorDBValue": wizardMetaData?.id
          })
        }
        if (inputParams.length == 0) {
          dispatch(setFileDataHasErrors(false))
          dispatch(setFileDataHasWarnings(false))
          dispatch(setFileUploadHasErrors(false))
          errors = false;
          warnings = false;
        } else {
          let msg = ""
          inputParams.forEach((element: any) => {
            let inputParam = element["inputParam"]
            let sheetValue = element["sheetValue"]
            let sensorDBValue = element["sensorDBValue"]
            msg += `The ${inputParam} in your input sheet (${sheetValue}) does not match this sensor's ${inputParam} (${sensorDBValue}) \n`;
          })
          const dispatchMessage = {
            text: msg,
            color: ARGOS_ERROR
          }
          dispatch(fileUploadAddMessage(dispatchMessage))
          _fileUploadHasErrors = true
          dispatch(setFileUploadHasErrors(true))
        }
      }

      /**
       * WizardMetaData is the data that is passed to the wizard when it is opened. It contains wizardType and properties for that wizard type like ids.
       * Response is the data that is returned from the server after the file is uploaded.
       */
      Object.assign(response, wizardMetaData)
      dispatch(setFileUploadResponse(response))

      let statementMessagesList = response?.all_statements_list
      for (const i in statementMessagesList) {
        const msgList = statementMessagesList[i]
        if (msgList.length > 0) {
          if (wizardMetaData?.wizardType === 'Vehicle Program' && i === '0') continue
          warnings = true
          break
        }
      }

      let warningMessagesList = response?.all_warnings_list
      for (const i in warningMessagesList) {
        const msgList = warningMessagesList[i]
        if (msgList.length > 0) {
          if (wizardMetaData?.wizardType === 'Vehicle Program' && i === '0') continue
          warnings = true
          break
        }
      }
      let errorMessagesList = response?.all_errors_list
      for (const i in errorMessagesList) {
        const msgList = errorMessagesList[i]
        if (msgList.length > 0) {
          if (wizardMetaData?.wizardType === 'Vehicle Program' && i === '0') continue
          errors = true
          break
        }
      }

      if (statements) {
        if (dispatch(setFileUploadHasErrors(false)))
          dispatch(fileUploadAddMessage(msg))
        dispatch(setFileDataHasErrors(true))
      } else {
        dispatch(setFileDataHasStatements(false))
      }

      if (wizardMetaData?.wizardType === 'Vehicle Program') {
        if (statementMessagesList != null && statementMessagesList?.[0].length == 0) {
          let statementMessagesListClone = cloneDeep(errorMessagesList)
          statementMessagesListClone.shift()
          errorMessagesList = cloneDeep(statementMessagesListClone)
        }
      }

      if (errors && !_fileUploadHasErrors) {
        const msg = {text: `Errors found, check next steps to see error messages`, color: ARGOS_ERROR}
        dispatch(fileUploadAddMessage(msg))
        dispatch(setFileDataHasErrors(true))
      } else {
        dispatch(setFileDataHasErrors(false))
      }
      if (wizardMetaData?.wizardType === 'Vehicle Program') {
        if (errorMessagesList[0].length == 0) {
          let errorMessagesListClone = cloneDeep(errorMessagesList)
          errorMessagesListClone.shift()
          errorMessagesList = cloneDeep(errorMessagesListClone)
        }
      }
      const statementLst: IMessage[][] = fileProcessingLogsToObjects(statementMessagesList)
      dispatch(setAllStatementMessages(statementLst))

      const lst: IMessage[][] = fileProcessingLogsToObjects(errorMessagesList)
      dispatch(setAllErrorMessages(lst))
      if (warnings) {
        const msg = {text: `Warnings found, check next steps to see warning messages`, color: ARGOS_ERROR}
        dispatch(fileUploadAddMessage(msg))
        dispatch(setFileDataHasWarnings(true))
      } else {
        dispatch(setFileDataHasWarnings(false))
      }
      if (wizardMetaData?.wizardType === 'Vehicle Program') {
        if (warningMessagesList != null && warningMessagesList[0].length == 0) {
          let warningMessagesListClone = cloneDeep(warningMessagesList)
          warningMessagesListClone.shift()
          warningMessagesList = cloneDeep(warningMessagesListClone)
        }
      }

      const lst2: IMessage[][] = fileProcessingLogsToObjects(warningMessagesList)
      dispatch(setAllWarningMessages(lst2))

      if (vehicleProgramHasDuplicateSensors) {
        const msg = {
          text: duplicateSensorsErrorMsg,
          color: ARGOS_ERROR
        }
        dispatch(fileUploadAddMessage(msg))
        dispatch(setFileUploadHasErrors(true))
      }
    }
  }

  const getUploadParams = async () => {
    const idTok = await requestIdToken(instance, accounts);
    let fields
    if (wizardMetaData?.wizardType === 'Vehicle Program') {
      fields = {
        vehicleProgramId: wizardMetaData?.vehicleProgramId,
      }
    }

    return {
      url: props.uploadFileUrl,
      headers: {
        "Authorization": `Bearer ${idTok}`
      },
      fields
    }
  }

  const content1 = <FileUploadLoaderIcon isFileUploading={isFileUploading}/>
  const content2 = (!isFileUploading ?
    <Dropzone
      getUploadParams={getUploadParams}
      onChangeStatus={(file: IFileWithMeta, status: StatusValue, allFiles: IFileWithMeta[]) => {
        handleChangeStatus(file, status, allFiles)
      }}
      inputContent="Drag and Drop Excel File or Click to Browse"
      maxFiles={1}
      multiple={false}
      accept=".xlsx"
      styles={{
        inputLabel: {
          color: 'grey'
        }
      }}
      canCancel={false}
    /> : <Skeleton animation="wave" height={105} variant="rectangular"/>)

  const overrideDataWarningOpen = useAppSelector(state => selectDialogState(state).isOverrideVersionDataWarningDialogOpen)

  return (
    <>
      <TemplateStep content1={content1} content2={content2} statementMessages={statementMessages} errorMessages={errorMessages} warningMessages={warningMessages}
                    messagesTitle={messagesTitle} isFileUploading={isFileUploading} isFirstWizardStep={true}
                    id={id ?? ""}
                    isLastWizardStep={false}
                    customTopRow={null}
      />

      <StyledArgosDialog
        width={'70vw'}
        height={'70vh'}
        open={overrideDataWarningOpen}
      >
        <OverrideDataWarningDialog onClickCancel={() => {
          dispatch(setWizardOpen(false))
          dispatch(clearState())
        }}

        />
      </StyledArgosDialog>
    </>
  );
};

export default StepUploadFile

/**
 * Recursively loop through tree to find if adas_program_id and development_stage already exist. If they do, return true, else return false.
 * @param adasProgramId
 * @param developmentStageName
 */
const checkTree = (tree: any, developmentStageId: string) => {
  if (tree == null) return false

  if (tree?.developmentStageId === developmentStageId && tree?.type === 'devStageFolder')
    return true
  const children = tree?.children
  if (children == null) return false
  for (const child of children) {
    const isOverride = checkTree(child, developmentStageId)
    if (isOverride) return true
  }
  return false
}